import React, { FC, PropsWithChildren, CSSProperties } from 'react';
import { UseMutationResult } from 'react-query';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { FormCtrl, useValidatedForm, Validations } from './hooks';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { useTranslate } from '@im/react-localization';

export interface ButtonSetProps<T> {
  formCtrl: FormCtrl<T>;
  isLoading: boolean;
  onClose: (result?: any) => void;
  submitLabel: string;
}

export const ButtonSet: FC<ButtonSetProps<any>> = ({
  formCtrl: { isValid, submittedOnce },
  isLoading,
  onClose,
  submitLabel,
}) => {
  const translate = useTranslate();
  return (
    <>
      <Button
        disabled={isLoading}
        onClick={() => onClose()}
        variant="newOutlined"
      >
        {translate('Common.Cancel')}
      </Button>
      <LoadingButton
        disabled={!isValid && submittedOnce}
        loading={isLoading}
        type="submit"
        variant="newContained"
      >
        {submitLabel}
      </LoadingButton>
    </>
  );
};

export interface FormProps<T> {
  components?: {
    buttonSet?: (props: ButtonSetProps<T>) => any;
  };
  fields: (props: FormCtrl<Partial<T>>) => any;
  getErrorMsg?: (error: any, msg: string) => string | void;
  info?: (props: FormCtrl<T>) => any;
  initialState: T;
  mutation: UseMutationResult<any, any, T, any>;
  onClose: (result?: any) => void;
  submitLabel: string;
  sx?: CSSProperties;
  title?: string;
  validations?: Validations<T>;
}

function Form<T>({
  fields,
  components: { buttonSet } = {},
  getErrorMsg = (error, msg) => msg,
  initialState,
  mutation: { data, error, isError, isLoading, isSuccess, mutateAsync },
  onClose,
  submitLabel,
  sx = {},
  title,
  info,
  validations = {},
}: PropsWithChildren<FormProps<T>>) {
  const formCtrl = useValidatedForm<T>({
    initialState,
    onSubmit: (data) => mutateAsync(data).then(onClose).catch(handleError),
    validations,
  });

  function handleError(err: any) {
    formCtrl.setErrorOverrides(err?.response?.errors || {});
  }

  const { handleSubmit } = formCtrl;

  const errorMsg =
    isError && getErrorMsg(error, error.response?.message || error.message);

  return (
    <Box
      sx={{
        height: '100%',
        px: 3,
        py: 2,
        width: 450,
        ...sx,
      }}
    >
      <Stack height="100%" justifyContent="space-between">
        <Snackbar />
        {title && (
          <>
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography
                component="h3"
                fontSize={20}
                sx={{ mb: 2.7, mt: 0.85 }}
                variant="h2"
              >
                {title}
              </Typography>
              <IconButton
                onClick={() => onClose()}
                size="small"
                sx={{ alignSelf: 'baseline' }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <Divider />
          </>
        )}
        <Stack alignItems={'center'}>
          {errorMsg && (
            <Alert severity="error" variant="standard">
              {errorMsg}
            </Alert>
          )}
          {info && !errorMsg && (
            <Alert severity="info" variant="standard">
              {info(formCtrl)}
            </Alert>
          )}
        </Stack>
        <StyledForm aria-label="form" onSubmit={handleSubmit}>
          <Stack gap={2} height="100%" justifyContent="space-between">
            <StyledFieldset disabled={isLoading}>
              {fields(formCtrl)}
            </StyledFieldset>
            <Stack direction="row" gap={2} justifyContent="flex-end">
              {buttonSet ? (
                buttonSet({ formCtrl, isLoading, onClose, submitLabel })
              ) : (
                <ButtonSet {...{ formCtrl, isLoading, onClose, submitLabel }} />
              )}
            </Stack>
          </Stack>
        </StyledForm>
      </Stack>
    </Box>
  );
}

export default Form;

export const StyledFieldset = styled('fieldset')(({ theme }) => ({
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  margin: 0,
  overflowY: 'auto',
  padding: 0,
}));

export const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  marginTop: theme.spacing(1),
  overflow: 'hidden',
}));
